import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { ScreenNumbersAndUsersResult } from '@app/core/interfaces/screen-numbers-and-users-result';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { PhoneNumber } from '@business/phone-number/models/phone-number';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ScreenNumberRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(screenNumber: Partial<ScreenNumber>): Observable<void> {
    return this.utils.apiUrl(`/screenNumbers`).pipe(
      mergeMap((url) => this.http.post<void>(url, screenNumber)),
      wrapError('screen-number-repository:add')
    );
  }

  addToUser(loginName: string, screenNumberId: number, aliasesToo: boolean): Observable<void> {
    return this.utils.apiUrl(`/users/${loginName}/screenNumbers/${screenNumberId}`).pipe(
      mergeMap((url) =>
        this.http.post<void>(url, {
          aliasesToo,
        })
      ),
      wrapError('screen-number-repository:add-screen-number-to-user')
    );
  }

  assignAsMain(externalNumber: PhoneNumber, loginName: string): Observable<void> {
    return this.utils.apiUrl(`/users/${loginName}/phoneNumbers/${externalNumber.id}/setAsMainNumber`).pipe(
      mergeMap((url) => this.http.put<void>(url, externalNumber)),
      wrapError('screen-number-repository:assign-as-main')
    );
  }

  delete(id: number): Observable<void> {
    return this.utils.apiUrl(`/screenNumbers/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('screen-number-repository:delete')
    );
  }

  deleteFromUser(loginName: string, screenNumberId: number): Observable<void> {
    return this.utils.apiUrl(`/users/${loginName}/screenNumbers/${screenNumberId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('screen-number-repository:delete-screen-number-from-user')
    );
  }

  get(id: number): Observable<ScreenNumber> {
    return this.utils.apiUrl(`/screenNumbers/${id}`).pipe(
      mergeMap((url) => this.http.get<ScreenNumber>(url)),
      wrapError('screen-number-repository:get')
    );
  }

  listScreenNumbers(): Observable<ScreenNumber[]> {
    return this.utils.routeBasedApiUrl('/screenNumbers', UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) => this.http.get<ScreenNumber[]>(url)),
      wrapError('screen-number-repository:list-screen-numbers')
    );
  }

  listScreenNumbersAndUsers(): Observable<ScreenNumbersAndUsersResult> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]:
        BaseUrls.ServiceProvider.Enterprise + '/screenNumbersAndUsers',
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + '/screenNumbersAndUsers',
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.get<ScreenNumbersAndUsersResult>(url)),
      wrapError('screen-number-repository:list-screen-numbers-and-users')
    );
  }

  listScreenNumberUsers(screenNumberId: number, filterAsMainNumber?: boolean): Observable<EndUserWithId[]> {
    let url: string;

    if (filterAsMainNumber !== undefined) {
      url = `/screenNumbers/${screenNumberId}/users?isMainNumber=${filterAsMainNumber}`;
    } else {
      url = `/screenNumbers/${screenNumberId}/users`;
    }

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      wrapError('screen-number-repository:list-screen-number-users')
    );
  }

  listUsersNotHavingScreenNumber(screenNumberId: number): Observable<EndUserWithId[]> {
    return this.utils.apiUrl(`/screenNumbers/${screenNumberId}/usersNotHavingScreenNum`).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      wrapError('screen-number-repository:list-users-not-having-screen-number')
    );
  }

  update(screenNumber: ScreenNumber): Observable<void> {
    return this.utils.apiUrl(`/screenNumbers/${screenNumber.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, screenNumber)),
      wrapError('screen-number-repository:update')
    );
  }
}
